import { API_PREFIX, CSRF_HEADER, type ApiError } from "@ziolo/shared";

/**
 * Thin fetch wrapper. Sends cookies, attaches the CSRF token on mutations, and turns
 * non-2xx responses into a typed error instead of a silently ignored result.
 */

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string> | undefined;

  constructor(status: number, body: ApiError) {
    super(body.error.message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = body.error.code;
    this.fields = body.error.fields;
  }
}

let csrfToken: string | null = null;

/** Set by the session provider from every auth response. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * Message to show the user. API errors already carry a Polish message written for
 * them; anything else means the request never got an answer.
 */
export function errorMessage(cause: unknown): string {
  return cause instanceof ApiRequestError ? cause.message : "Brak połączenia z serwerem";
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiRequestError(response.status, payload as ApiError);
  }

  return payload as T;
}

export async function apiRequest<T>(
  path: string,
  { method = "GET", body, signal }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (method !== "GET" && csrfToken) {
    headers[CSRF_HEADER] = csrfToken;
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    method,
    headers,
    credentials: "include",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  return parseResponse<T>(response);
}

/** Multipart upload. Do not set Content-Type — the browser adds the boundary. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  if (csrfToken) headers[CSRF_HEADER] = csrfToken;

  const response = await fetch(`${API_PREFIX}${path}`, {
    method: "POST",
    headers,
    credentials: "include",
    body: form,
  });

  return parseResponse<T>(response);
}
